import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";

import { useStoreContext } from "@components/store/provider";

import { ProductListingContext } from "@components/store/products/listing/listing-provider";

import {
  getFormattedBrandValues,
  getFormattedOptionValues,
  getFormattedProductionDays,
  getItemFeatures,
  getPriceSliderRanges,
  getFormattedPrintTypeValues,
} from "@helpers/products/search";
import { isCustomCurrency } from "@stadium/helpers/currency";

import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import {
  getIsLockerShop,
  getStoreCurrencyName,
  getStoreCurrencyPrefix,
  getStoreCurrencySuffix,
  getIsHideBulkPricing,
} from "@selectors/store";

const emptyObj = {};

const useSetFilters = ({ loading, aggregations }) => {
  const {
    setPriceRanges,
    setRequiredDietaryRestrictionIds,
    setColorOptionValues,
    setSizeOptionValues,
    setBrandValues,
    setProductionDays,
    setBulkProductsCount,
    setOnDemandProductsCount,
    setCoreOnDemandProductsCount,
    setItemFeatures,
    setVolumeDiscountProductsCount,
    setPrintTypeValues,
  } = useContext(ProductListingContext);

  const { convertPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();
  const { storeDisplayCurrencyCode } = useStoreContext();
  const storeCurrencyName = useSelector(getStoreCurrencyName);
  const storeCurrencySuffix = useSelector(getStoreCurrencySuffix);
  const storeCurrencyPrefix = useSelector(getStoreCurrencyPrefix);
  const isHideBulkPricing = useSelector(getIsHideBulkPricing);
  const isLockerShop = useSelector(getIsLockerShop);

  useEffect(() => {
    if (!loading && aggregations) {
      const dietaryRestrictionIds = (
        aggregations.external_dietary_restrictions || aggregations.dietary_restrictions
      ).buckets.map((data) => data.key);
      const currencySuffixOrPrefix = storeCurrencySuffix || storeCurrencyPrefix;
      setPriceRanges({
        ...getPriceSliderRanges(
          aggregations?.price_range?.usd_price_stats || emptyObj,
          convertPriceInDisplayStoreCurrency,
        ),
        currencyCode: storeDisplayCurrencyCode,
        currencyName: storeCurrencyName,
        currencySymbol: !isCustomCurrency(storeDisplayCurrencyCode)
          ? storeDisplayCurrencyCode || currencySuffixOrPrefix
          : currencySuffixOrPrefix,
      });

      setRequiredDietaryRestrictionIds(dietaryRestrictionIds);
      setBrandValues(getFormattedBrandValues(aggregations.brands || []));
      setColorOptionValues(
        getFormattedOptionValues(aggregations?.option_values?.filtered_color_values?.color_values),
      );
      setSizeOptionValues(
        getFormattedOptionValues(aggregations?.option_values?.filtered_size_values?.size_values),
      );

      setProductionDays(getFormattedProductionDays(aggregations.production_days || []));
      setBulkProductsCount(Number(aggregations.bulk_available?.doc_count || 0));
      setOnDemandProductsCount(Number(aggregations.no_min_quantity?.doc_count || 0));

      const printValues = isLockerShop
        ? getFormattedPrintTypeValues(aggregations?.print_types)
        : [];
      setPrintTypeValues(printValues);

      const coreOnDemandProductsCount = isLockerShop
        ? Number(aggregations.core_on_demand?.doc_count || 0)
        : 0;
      setCoreOnDemandProductsCount(coreOnDemandProductsCount);

      setVolumeDiscountProductsCount(
        isHideBulkPricing ? 0 : Number(aggregations.has_tier_pricing?.doc_count || 0),
      );
      const ecoFriendlyProductCount = Number(aggregations.eco_friendly?.doc_count || 0);
      const sustainableProductCount = Number(aggregations.sustainable?.doc_count || 0);
      const shippedFromStadiumProductsCount = Number(
        aggregations.shipped_from_stadium?.doc_count || 0,
      );
      setItemFeatures(
        getItemFeatures({
          ecoFriendlyProductCount,
          sustainableProductCount,
          shippedFromStadiumProductsCount,
        }),
      );
    }
  }, [
    loading,
    aggregations,
    setPriceRanges,
    storeDisplayCurrencyCode,
    setRequiredDietaryRestrictionIds,
    setColorOptionValues,
    setSizeOptionValues,
    setBrandValues,
    setProductionDays,
  ]);

  return null;
};

export default useSetFilters;
